import React, { useState } from "react";
import { Plus, Check } from "lucide-react";
import { useBuilder, BlockConfig } from "../BuilderContext";
import PremiumModal from "../../../ui/PremiumModal";

const fmt = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const TimeEntriesTableBlock: React.FC<{ block: BlockConfig }> = ({ block }) => {
  const { state, dispatch } = useBuilder();
  const heading = block.settings.headingOverride || "Time Entries";
  const [showPicker, setShowPicker] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const timeItems = state.lineItems.filter((li) => li.kind === "time");

  const toggleEntry = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddSelected = () => {
    for (const te of state.unbilledTimeEntries) {
      if (selectedIds.has(te._id)) {
        dispatch({
          type: "ADD_LINE_ITEM",
          item: {
            kind: "time",
            description: `${te.narrative || "Time entry"} (${te.minutes} min)`,
            quantity: te.minutes / 60,
            unitPriceCents: te.rateCents || 0,
            amountCents: te.amountCents || 0,
            timeEntryIds: [te._id],
            taxable: true,
            taxAmountCents: 0,
          },
        });
      }
    }
    setShowPicker(false);
    setSelectedIds(new Set());
  };

  return (
    <div className="py-4">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">{heading}</p>
      {timeItems.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2" style={{ borderColor: "var(--color-primary)" }}>
              <th className="text-left py-2 text-xs font-semibold text-slate-600">Description</th>
              <th className="text-right py-2 text-xs font-semibold text-slate-600 w-20">Hours</th>
              <th className="text-right py-2 text-xs font-semibold text-slate-600 w-24">Rate</th>
              <th className="text-right py-2 text-xs font-semibold text-slate-600 w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            {timeItems.map((li, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2 text-slate-800">{li.description}</td>
                <td className="py-2 text-right text-slate-600">{li.quantity.toFixed(2)}</td>
                <td className="py-2 text-right text-slate-600">{fmt(li.unitPriceCents)}/hr</td>
                <td className="py-2 text-right font-medium text-slate-800">{fmt(li.amountCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-slate-400 py-4 text-center">No time entries included</p>
      )}

      {state.unbilledTimeEntries.length > 0 && (
        <div className="mt-3 print:hidden">
          <button
            onClick={() => setShowPicker(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add Time Entries
          </button>
        </div>
      )}

      <PremiumModal
        open={showPicker}
        onClose={() => setShowPicker(false)}
        title="Select Time Entries"
        icon={Plus}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowPicker(false)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSelected}
              disabled={selectedIds.size === 0}
              className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              Add {selectedIds.size} Entries
            </button>
          </div>
        }
      >
        <div className="space-y-1">
          {state.unbilledTimeEntries.map((te: any) => (
            <button
              key={te._id}
              onClick={() => toggleEntry(te._id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                selectedIds.has(te._id) ? "border-brand-accent bg-brand-light" : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                selectedIds.has(te._id) ? "border-brand-accent bg-brand-accent" : "border-slate-300"
              }`}>
                {selectedIds.has(te._id) && <Check className="h-3 w-3 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800 truncate">{te.narrative || "Time entry"}</p>
                <p className="text-xs text-slate-500">{te.minutes} min  -  {new Date(te.date).toLocaleDateString()}</p>
              </div>
              <span className="text-sm font-medium text-slate-700">{fmt(te.amountCents || 0)}</span>
            </button>
          ))}
        </div>
      </PremiumModal>
    </div>
  );
};

export default TimeEntriesTableBlock;
